import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { AUTH } from "@/constants/testIds";
import { ArrowRight, AlertCircle, Briefcase, User } from "lucide-react";

export default function Register() {
  useSEO({
    title: "Create your ErrandDay account",
    description: "Join ErrandDay in under a minute. Send tasks home from anywhere, or sign up as a verified agent and earn doing trusted errands.",
    noindex: true,
    canonicalPath: "/register",
  });
  const { registerEmail } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const presetRole = params.get("role") === "agent" ? "agent" : "client";

  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "", country: "",
    role: presetRole,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const user = await registerEmail(form);
      nav(user.role === "agent" ? "/agent" : "/dashboard");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = () => {
    sessionStorage.setItem("eb_google_role", form.role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden md:flex md:w-5/12 relative overflow-hidden border-r border-[var(--eb-border)]">
        <img
          src="https://images.unsplash.com/photo-1614023342667-6f060e9d1e04?w=1200&q=80&auto=format&fit=crop"
          alt="Trusted agent"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--eb-bg)] via-[var(--eb-bg)]/80 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-md bg-[var(--eb-primary)] flex items-center justify-center text-white font-black">ED</div>
            <span className="font-[800] text-lg tracking-tight">ErrandDay</span>
          </Link>
          <h1 className="text-4xl md:text-5xl leading-tight">Your <span className="text-[var(--eb-primary)]">first task</span><br />is just minutes away.</h1>
          <p className="mt-4 text-[var(--eb-text-dim)] max-w-md">Verified agents, escrow protection, photo proof. Join the diaspora using ErrandDay.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg eb-fadein">
          <Link to="/" className="md:hidden inline-flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-md bg-[var(--eb-primary)] flex items-center justify-center text-white font-black">ED</div>
            <span className="font-[800] text-lg">ErrandDay</span>
          </Link>
          <span className="eb-chip">Get started</span>
          <h2 className="mt-4 text-3xl">Create your account</h2>
          <p className="mt-2 text-[var(--eb-text-dim)] text-sm">Already with us? <Link to="/login" data-testid={AUTH.toggleLink} className="text-[var(--eb-primary)] hover:underline">Sign in</Link>.</p>

          <div className="mt-7 grid grid-cols-2 gap-2">
            <RoleCard
              testId={AUTH.roleClient}
              active={form.role === "client"}
              onClick={() => setForm({ ...form, role: "client" })}
              icon={User}
              title="I'm a Client"
              body="Send tasks home"
            />
            <RoleCard
              testId={AUTH.roleAgent}
              active={form.role === "agent"}
              onClick={() => setForm({ ...form, role: "agent" })}
              icon={Briefcase}
              title="I'm an Agent"
              body="Earn doing errands"
            />
          </div>

          <form onSubmit={onSubmit} className="mt-6 space-y-3">
            <div>
              <label className="eb-label">Full name</label>
              <input data-testid={AUTH.nameInput} required value={form.name} onChange={set("name")} className="eb-input" placeholder="Emeka Obi" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="eb-label">Email</label>
                <input data-testid={AUTH.emailInput} type="email" required value={form.email} onChange={set("email")} className="eb-input" placeholder="you@email.com" />
              </div>
              <div>
                <label className="eb-label">Phone</label>
                <input data-testid={AUTH.phoneInput} value={form.phone} onChange={set("phone")} className="eb-input" placeholder="+1 555…" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="eb-label">Password</label>
                <input data-testid={AUTH.passwordInput} type="password" required minLength={6} value={form.password} onChange={set("password")} className="eb-input" placeholder="6+ characters" />
              </div>
              <div>
                <label className="eb-label">{form.role === "agent" ? "City" : "Country"}</label>
                <input data-testid={AUTH.countryInput} value={form.country} onChange={set("country")} className="eb-input" placeholder={form.role === "agent" ? "Lagos" : "United States"} />
              </div>
            </div>

            {error && (
              <div data-testid={AUTH.errorAlert} className="flex items-start gap-2 text-sm text-[var(--eb-secondary)] bg-[var(--eb-secondary)]/10 border border-[var(--eb-secondary)]/30 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
              </div>
            )}

            <button data-testid={AUTH.submitBtn} disabled={busy} type="submit" className="eb-btn eb-btn-primary w-full !py-3 mt-3">
              {busy ? "Creating…" : (<>Create account <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-[var(--eb-text-dim)]">
            <div className="eb-divider flex-1" /> OR <div className="eb-divider flex-1" />
          </div>
          <button data-testid={AUTH.googleBtn} onClick={onGoogle} className="eb-btn eb-btn-ghost w-full !py-3">
            Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ active, onClick, icon: Icon, title, body, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`eb-elev p-4 text-left transition-all ${active ? "border-[var(--eb-primary)] ring-2 ring-[var(--eb-primary)]/30" : "hover:border-[var(--eb-primary)]"}`}
    >
      <Icon className={`w-5 h-5 ${active ? "text-[var(--eb-primary)]" : "text-[var(--eb-text-dim)]"}`} />
      <div className="mt-2 font-[700]">{title}</div>
      <div className="text-xs text-[var(--eb-text-dim)] mt-1">{body}</div>
    </button>
  );
}
